<template>
    <div>
        <v-autocomplete
            ref="select"
            v-bind="attrs"
            v-on="$listeners"
            :loading="
                (firstLoad && !items.length) ||
                    fetchingCache ||
                    isHydratingSelection
            "
            :search-input.sync="search"
            :no-filter="true"
            @click:clear="onClickClear"
            :return-object="returnObject"
        >
            <!-- Custom selection slot -->
            <template
                v-if="customSelection"
                v-slot:selection="{ item, on, attrs }"
            >
                <slot name="selection" v-bind="{ item, on, attrs }"></slot>
            </template>

            <!-- Custom item slot -->
            <template v-if="customItem" v-slot:item="{ item, on, attrs }">
                <slot name="item" v-bind="{ item, on, attrs }"></slot>
            </template>

            <!-- Select All option for multiple selection -->
            <template v-if="multiple" #prepend-item>
                <v-list-item ripple @mousedown.prevent @click="selectAllItems">
                    <v-list-item-action>
                        <v-icon
                            :color="
                                attrs.items.length > 0 ? 'indigo darken-4' : ''
                            "
                        >
                            {{ icon }}
                        </v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                        <v-list-item-title>
                            {{ $t('forms.selectAll') }}
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
                <v-divider class="mt-2"></v-divider>
            </template>

            <!-- Infinite scroll loading -->
            <template #append-item>
                <SimpleLoading
                    v-if="
                        (!isFirstPage && hasNextPage && expanded) ||
                            (firstLoad && items.length)
                    "
                    :infinite-scroll="infiniteScroll"
                ></SimpleLoading>
            </template>

            <!-- No data message -->
            <template #no-data>
                <div
                    :class="[
                        'text-center text-caption px-4',
                        { 'pb-2': firstLoad && items.length },
                    ]"
                >
                    <template v-if="firstLoad && !items.length">
                        {{ $t('forms.loadingOptions') }}
                    </template>
                    <template v-else>
                        {{ $t('forms.noOptionsFound') }}
                    </template>
                </div>
            </template>

            <!-- Append outer slot -->
            <template #append-outer>
                <slot name="append-outer" />
            </template>
        </v-autocomplete>

        <!-- Hidden inputs for form submission -->
        <input
            v-for="(value, index) in hiddenValues"
            :key="index"
            type="hidden"
            :name="hiddenName"
            :value="value"
        />
    </div>
</template>

<script>
import SimpleLoading from './SimpleLoading.vue'
import Model from '@/shared/orm/Model'

export default {
    inheritAttrs: false,

    components: {
        SimpleLoading,
    },

    props: {
        // Value passed from v-model
        value: {
            required: true,
        },

        // Model class instance for API calls
        model: {
            required: true,
            validator: model => model instanceof Model,
        },

        // Field to use as display text
        itemText: {
            type: String,
            required: true,
        },

        // Field to use as value
        itemValue: {
            type: String,
            default: 'id',
        },

        // Field name for form submission
        name: {
            type: String,
            required: true,
        },

        // Enable multiple selection
        multiple: {
            type: Boolean,
            default: false,
        },

        // Label for the field
        label: {
            type: String,
        },

        // Placeholder text
        placeholder: {
            type: String,
        },

        // Enable custom selection template
        customSelection: {
            type: Boolean,
            default: false,
        },

        // Enable custom item template
        customItem: {
            type: Boolean,
            default: false,
        },

        // Return object instead of just value
        returnObject: {
            type: Boolean,
            default: true,
        },

        // Items per page for pagination
        perPage: {
            type: Number,
            default: 15,
        },

        // Enable persistent mode (keep data when closed)
        persistent: {
            type: Boolean,
            default: true,
        },

        // Additional filters to apply to queries
        filters: {
            type: Object,
            default: () => ({}),
        },

        // Items to ignore from results
        ignoreItems: {
            type: Array,
            default: () => [],
        },

        // Items to include
        withRelations: {
            type: Array,
            default: () => [],
        },
    },

    data: () => ({
        hiddenValues: [],
        currentPage: 0,
        lastPage: 1,
        firstLoad: false,
        items: [],
        expanded: false,
        fetchingItems: false,
        search: null,
        rootSearch: null,
        cache: [],
        fetchingCache: false,
        triedRecoverIds: false,
        refMenu: undefined,
        isCached: false,
        debPreSearch: null,
        isHydratingSelection: false,
    }),

    created() {
        // Debounce search input to avoid too many API calls
        this.debPreSearch = this.$lodash.debounce(() => {
            this.rootSearch = this.search
        }, 500)

        // Initialize value for multiple mode
        if (this.multiple && !Array.isArray(this.value)) {
            this.$emit('input', [])
        }

        // Hydrate selection on component creation
        if (
            this.value &&
            (Array.isArray(this.value) ? this.value.length > 0 : true)
        ) {
            this.hydrateSelectionByItemValue()
        }

        // Load initial items if no value or multiple mode
        if (!this.value || this.multiple) {
            this.fetchItems()
        }
    },

    mounted() {
        this.$set(this.$data, 'refMenu', this.$refs.select)
    },

    computed: {
        // Computed attributes for v-autocomplete
        attrs() {
            const { disabled, ...attrs } = this.$attrs

            this.setHiddenValues(this.value) // Use this.value instead of $attrs.value

            const computedAttrs = {
                ...{
                    value: this.value, // ← FIX: Use this.value from props
                    items: this.mergeItems,
                    multiple: this.multiple,
                    'item-text': this.itemText,
                    'item-value': this.itemValue,
                    disabled: this.fetchingCache || disabled,
                    autocomplete: 'no',
                    label: this.label,
                    placeholder: this.computedPlaceholder,
                    clearable: this.multiple,
                    'return-object': this.returnObject,
                    'value-comparator': this.compareByItemValue,
                },
                ...attrs,
            }

            return computedAttrs
        },

        // Computed placeholder
        computedPlaceholder() {
            return (
                this.placeholder || this.$t('forms.placeHolders.typeToSearch')
            )
        },

        // Hidden input name for form submission
        hiddenName() {
            return this.multiple ? `${this.name}[]` : this.name
        },

        // Check if has next page
        hasNextPage() {
            return this.lastPage > this.currentPage
        },

        // Check if is first page
        isFirstPage() {
            return this.currentPage === 0
        },

        // Merge items and cache, removing duplicates
        mergeItems() {
            const filtered = this.$lodash.uniqBy(
                [...this.items, ...this.cache],
                this.itemValue,
            )
            const result = this.filterIgnoredItems(filtered)

            return result
        },

        // Check if all items are selected
        likesAllItems() {
            return (
                this.items &&
                this.attrs.value &&
                this.attrs.value.length === this.items.length
            )
        },

        // Check if some items are selected
        likesSomeItems() {
            return (
                this.attrs.value &&
                this.attrs.value.length > 0 &&
                !this.likesAllItems
            )
        },

        // Icon for select all button
        icon() {
            if (this.likesAllItems) return 'mdi-close-box'
            if (this.likesSomeItems) return 'mdi-minus-box'
            return 'mdi-checkbox-blank-outline'
        },
    },

    watch: {
        // Watch search input changes
        search() {
            this.debPreSearch()
        },

        // Watch root search changes
        rootSearch() {
            if (this.expanded) {
                this.resetItems()
                this.fetchItems()
            }
        },

        // Watch menu active state
        'refMenu.isMenuActive'(value) {
            if (value) this.onOpen()
            else this.onClose()
        },

        // Reset list index for multiple selection
        'refMenu.$refs.menu.listIndex'(value) {
            if (this.multiple && value !== -1) {
                this.refMenu.$refs.menu.listIndex = -1
            }
        },

        // Watch value changes for hydration
        value(val) {
            if (val && !this.isHydratingSelection) {
                // Need hydration if value exists but not found in current items/cache
                const selections = Array.isArray(val) ? val : [val]
                const needsHydration = selections.some(sel => {
                    const id =
                        sel && typeof sel === 'object'
                            ? sel[this.itemValue]
                            : sel
                    if (!id) return false

                    return !this.mergeItems.some(
                        item => String(item[this.itemValue]) === String(id),
                    )
                })

                if (needsHydration) {
                    this.hydrateSelectionByItemValue()
                }
            }
        },
    },

    methods: {
        // Compare values for v-autocomplete - ESSENTIAL for Vuetify
        compareByItemValue(a, b) {
            const key = this.itemValue
            const getValue = v => (v && typeof v === 'object' ? v[key] : v)
            const normalize = v => (v == null ? null : String(v))

            // Handle arrays (multiple selection)
            if (Array.isArray(a) || Array.isArray(b)) {
                const arrA = Array.isArray(a) ? a : [a]
                const arrB = Array.isArray(b) ? b : [b]

                if (arrA.length !== arrB.length) return false

                const sortedA = arrA
                    .map(getValue)
                    .map(normalize)
                    .sort()
                const sortedB = arrB
                    .map(getValue)
                    .map(normalize)
                    .sort()

                return sortedA.every((val, i) => val === sortedB[i])
            }

            // Handle single values
            return normalize(getValue(a)) === normalize(getValue(b))
        },

        // Hydrate selection by fetching missing items - ESSENTIAL for pre-selection
        async hydrateSelectionByItemValue() {
            if (this.isHydratingSelection || !this.value) return
            this.isHydratingSelection = true

            try {
                const selections = Array.isArray(this.value)
                    ? this.value
                    : [this.value]
                const missingIds = []

                // Find IDs that are not in cache/items
                for (const sel of selections) {
                    const id =
                        sel && typeof sel === 'object'
                            ? sel[this.itemValue]
                            : sel
                    if (!id) continue

                    const exists = this.mergeItems.some(
                        item => String(item[this.itemValue]) === String(id),
                    )
                    if (!exists) missingIds.push(id)
                }

                if (!missingIds.length) {
                    return
                }

                // Fetch missing items individually to ensure all are found
                for (const id of missingIds) {
                    const modelInstance = new this.model.constructor()
                    const res = await modelInstance
                        .where(this.itemValue, id)
                        .get()

                    if (res && res.data && res.data.length) {
                        this.cache.push(...res.data)
                    }
                }

                // Rebuild selection with hydrated objects
                const hydrated = selections.map(sel => {
                    const id =
                        sel && typeof sel === 'object'
                            ? sel[this.itemValue]
                            : sel

                    const foundItem = this.mergeItems.find(
                        item => String(item[this.itemValue]) === String(id),
                    )

                    return foundItem || sel
                })

                // Only emit if selection changed
                const hasChanged = selections.some(
                    (sel, i) =>
                        JSON.stringify(sel) !== JSON.stringify(hydrated[i]),
                )

                if (hasChanged) {
                    const newValue = this.multiple ? hydrated : hydrated[0]
                    this.$emit('input', newValue)
                }

                // Force reactivity update to ensure mergeItems is recalculated
                await this.$nextTick()

                // Force v-autocomplete to update by triggering a re-computation
                if (this.$refs.select) {
                    this.$refs.select.$forceUpdate()
                }
            } catch (error) {
                console.warn('Hydration failed:', error)
            } finally {
                this.isHydratingSelection = false
            }
        },

        // Handle menu open
        async onOpen() {
            this.expanded = true
            if (!this.isFirstPage || this.firstLoad) return
            this.fetchItems()
        },

        // Handle menu close
        async onClose() {
            await new Promise(resolve => {
                const checkCached = () => {
                    if (this.isCached) {
                        resolve()
                    } else {
                        setTimeout(checkCached, 10)
                    }
                }
                checkCached()
            })
            this.expanded = false
        },

        // Handle clear button click
        async onClickClear() {
            this.$nextTick(() => {
                this.resetItems()
                this.fetchItems()
            })
        },

        // Handle infinite scroll
        async infiniteScroll([{ isIntersecting, target }]) {
            if (!isIntersecting || this.fetchingItems) return

            const ul = target.offsetParent
            const scrollTop = ul.scrollTop

            this.fetchItems(async () => {
                await this.$nextTick()
                ul && (ul.scrollTop = scrollTop)
            })
        },

        // Fetch items using model
        async fetchItems(callback = () => {}) {
            if (this.isFirstPage) this.firstLoad = true
            this.fetchingItems = true

            const search = this.rootSearch
            const page = this.currentPage + 1

            // Check if response is still valid
            const isInvalidResponse = () => {
                return (
                    this.rootSearch !== search ||
                    (!this.expanded && !this.persistent)
                )
            }

            try {
                // Create new model instance for the query
                const modelInstance = new this.model.constructor()

                // Apply search filter if exists
                if (search) {
                    modelInstance.where('search', search)
                }

                // Apply with relations if exists
                if (this.withRelations.length) {
                    this.withRelations.forEach(relation => {
                        modelInstance.with(relation)
                    })
                }

                // Apply additional filters
                Object.keys(this.filters).forEach(key => {
                    modelInstance.where(key, this.filters[key])
                })

                // Fetch paginated data
                const response = await modelInstance.paginate(
                    this.perPage,
                    page,
                )

                if (isInvalidResponse()) return false

                if (response && response.data) {
                    const dataItems = this.filterIgnoredItems(response.data)
                    this.items.push(...dataItems)

                    // Update pagination info
                    if (response.meta && response.meta.pagination) {
                        this.lastPage = response.meta.pagination.total_pages
                    } else {
                        this.lastPage = 1
                    }

                    this.currentPage++
                }

                return true
            } catch (error) {
                console.error('Error fetching items:', error)
                return false
            } finally {
                if (!isInvalidResponse()) {
                    this.firstLoad = false
                    this.fetchingItems = false
                    callback()
                }
            }
        },

        // Filter out ignored items
        filterIgnoredItems(items) {
            if (!this.ignoreItems.length) return items

            return items.filter(item => {
                const itemValue = item[this.itemValue]
                return !this.ignoreItems.includes(itemValue)
            })
        },

        // Set hidden values for form submission
        setHiddenValues(values) {
            this.isCached = false

            if (!Array.isArray(values)) values = [values]
            let arr = []

            values.forEach(value => {
                if (['string', 'number'].includes(typeof value)) {
                    arr.push(value)
                } else if (
                    value &&
                    typeof value === 'object' &&
                    this.itemValue in value
                ) {
                    const val = value[this.itemValue]
                    if (val > 0 || typeof val === 'string') arr.push(val)
                } else if (
                    value &&
                    typeof value === 'object' &&
                    'id' in value
                ) {
                    if (value.id > 0) arr.push(value.id)
                }
            })

            if (arr.length === 0 && !this.multiple) arr.push(null)

            if (this.$lodash.isEqual(arr, this.hiddenValues)) {
                this.isCached = true
            } else {
                this.hiddenValues = arr
                this.updateCache()
            }
        },

        // Update cache with selected items
        updateCache() {
            let notFoundInItems = []

            this.hiddenValues.forEach(value => {
                if (
                    value === null ||
                    this.cache.findIndex(
                        item => item[this.itemValue] === value,
                    ) !== -1
                )
                    return

                const item = this.items.find(
                    item => item[this.itemValue] === value,
                )

                if (!item) {
                    notFoundInItems.push(value)
                } else {
                    this.cache.push(this.$lodash.cloneDeep(item))
                }
            })

            this.checkCache()
            this.fetchCache(notFoundInItems)
            this.isCached = true
        },

        // Reset items and pagination
        resetItems() {
            this.items = []
            this.currentPage = 0
            this.lastPage = 1
        },

        // Clean up cache removing unselected items
        checkCache() {
            this.cache = this.cache.filter(item => {
                return (
                    this.hiddenValues.findIndex(
                        value => value == item[this.itemValue],
                    ) !== -1
                )
            })
        },

        // Fetch specific items by IDs for cache
        async fetchCache(ids = []) {
            if (ids.length === 0 || this.fetchingCache || this.triedRecoverIds)
                return

            this.triedRecoverIds = true
            this.fetchingCache = true

            try {
                for (const id of ids) {
                    const modelInstance = new this.model.constructor()
                    const response = await modelInstance
                        .where(this.itemValue, id)
                        .get()

                    if (response && response.data && response.data.length) {
                        this.cache.push(...response.data)
                    }
                }

                this.checkCache()
            } catch (error) {
                console.error('Error fetching cache:', error)
                this.$emit('cache-error', { name: this.name, error })
            } finally {
                this.fetchingCache = false
            }
        },

        // Select/deselect all items
        selectAllItems() {
            this.$nextTick(() => {
                let parsedItems

                if (this.likesAllItems) {
                    parsedItems = []
                } else {
                    parsedItems = this.mergeItems.slice()
                    if (!this.returnObject) {
                        parsedItems = parsedItems.map(
                            item => item[this.itemValue],
                        )
                    }
                }

                this.$emit('input', parsedItems)
            })
        },
    },
}
</script>
